import { useState, useMemo } from 'react';
import { useList, useCustomMutation, useCustom } from '@refinedev/core';
import {
  UserPlus,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  AlertCircle,
  User,
  Calendar,
  Phone,
  AtSign,
  Upload,
  Trash2,
  Eye,
  Copy,
  Globe,
  Bot,
  AppWindow,
  Link2,
  MailOpen,
  MailX,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { DataTable, TableToolbar, type Column, type SortState, type BulkAction } from '@/components/tables';
import { FormHeader } from '@/components/forms';
import { PricingTiersList } from '@/components/shared/PricingTiersList';
import { PricingTierBadge } from '@/components/shared/PricingTierBadge';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { notify } from '@/lib/notify';
import { type UserInvitation, type InvitationStatus, INVITATION_STATUS_LABELS, INVITATION_STATUS_COLORS, INVITATION_CHANNEL_LABELS } from './types';
import {
  MessageTemplateType,
  type MessageTemplate,
} from '@/resources/message-templates';
import { CreateInvitationDialog } from './CreateInvitationDialog';
import { CreateInvitationForUserDialog } from './CreateInvitationForUserDialog';
import { AttachInvitationToUserDialog } from './AttachInvitationToUserDialog';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { useScrollRestoration } from '@/hooks/use-scroll-restoration';

const API_URL = import.meta.env.VITE_API_URL || '';

/**
 * Иконка статуса
 */
function getStatusIcon(status: InvitationStatus) {
  switch (status) {
    case 'PENDING':
      return <Clock className="h-4 w-4 text-yellow-500" />;
    case 'SENT':
      return <Send className="h-4 w-4 text-blue-500" />;
    case 'ACCEPTED':
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    case 'FAILED':
      return <XCircle className="h-4 w-4 text-red-500" />;
    case 'EXPIRED':
      return <AlertCircle className="h-4 w-4 text-muted-foreground" />;
    default:
      return null;
  }
}

/**
 * Форматирование даты
 */
function formatDate(dateString: string | null) {
  if (!dateString) {return '—';}
  return new Date(dateString).toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Получить имя пользователя
 */
function getUserName(invitation: UserInvitation): string {
  const name = [invitation.telegramFirstName, invitation.telegramLastName].filter(Boolean).join(' ');
  if (name) {return name;}
  if (invitation.telegramUsername) {return `@${invitation.telegramUsername}`;}
  if (invitation.channel === 'EMAIL' && invitation.email) {return invitation.email;}
  if (invitation.telegramUserId !== null) {return `ID: ${invitation.telegramUserId}`;}
  return '—';
}

/**
 * Получить URL аватарки
 */
function getAvatarUrl(invitation: UserInvitation): string | null {
  if (!invitation.avatarFile?.path) {return null;}
  return `${API_URL}/uploads/${invitation.avatarFile.path}/avatar.jpg`;
}

/**
 * Диалог отправки приглашений
 */
function SendDialog({
  open,
  onClose,
  onConfirm,
  isLoading,
  selectedCount,
  templates,
  templatesLoading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (templateId?: number) => void;
  isLoading: boolean;
  selectedCount: number;
  templates: MessageTemplate[];
  templatesLoading: boolean;
}) {
  const [selectedTemplate, setSelectedTemplate] = useState<string>('default');

  const handleConfirm = () => {
    const templateId = selectedTemplate !== 'default' ? parseInt(selectedTemplate, 10) : undefined;
    onConfirm(templateId);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Отправить приглашения</DialogTitle>
          <DialogDescription>
            Будет отправлено {selectedCount} приглашений. Для Telegram-канала
            используется выбранный шаблон, для EMAIL — стандартное письмо со
            ссылкой на платформу. SENT EMAIL-приглашения переотправляются
            заново (токен и ссылка сохраняются).
          </DialogDescription>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <Label>Шаблон сообщения (только для Telegram)</Label>
            <Select
              value={selectedTemplate}
              onValueChange={setSelectedTemplate}
              disabled={templatesLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Выберите шаблон" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">По умолчанию</SelectItem>
                {templates.map((template) => (
                  <SelectItem key={template.id} value={String(template.id)}>
                    {template.name ?? template.slug ?? `#${template.id}`}
                    {template.isDefault && ' (по умолчанию)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Отмена
          </Button>
          <Button onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? 'Отправка...' : 'Отправить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Страница списка приглашений
 */
export const UserInvitationsList = () => {
  useScrollRestoration();
  const navigate = useNavigate();
  const [page, setPage] = usePersistedState('user-invitations:page', 1);
  const [sort, setSort] = usePersistedState<SortState | undefined>('user-invitations:sort', {
    field: 'createdAt',
    order: 'desc',
  });
  const [search, setSearch] = usePersistedState('user-invitations:search', '');
  const [statusFilter, setStatusFilter] = usePersistedState<string>('user-invitations:statusFilter', 'all');
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForUserDialogOpen, setCreateForUserDialogOpen] = useState(false);
  const [attachDialogInvitation, setAttachDialogInvitation] = useState<UserInvitation | null>(null);
  const [selectedInvitations, setSelectedInvitations] = useState<UserInvitation[]>([]);
  const [invitationsToDelete, setInvitationsToDelete] = useState<UserInvitation[]>([]);
  const pageSize = 20;

  // Проверка авторизованной сессии
  const sessionResult = useCustom<{ hasAuthorizedSession: boolean }>({
    url: '/user-invitations/session-status',
    method: 'get',
  });
  const hasSession = sessionResult.query.data?.data?.hasAuthorizedSession ?? false;

  // Загрузка шаблонов Telegram-приглашений из единого ресурса
  // message-templates (тип INVITATION_TELEGRAM). Контракт выбора по
  // id/slug не меняется — изменился только источник списка.
  const templatesResult = useCustom<MessageTemplate[]>({
    url: `/admin/message-templates/by-type/${MessageTemplateType.INVITATION_TELEGRAM}`,
    method: 'get',
  });
  const templates = ((): MessageTemplate[] => {
    const raw = templatesResult.query.data?.data;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.filter((t) => t.isActive);
  })();
  const templatesLoading = templatesResult.query.isLoading;

  // Подготовка фильтров
  const queryFilters = useMemo(() => {
    const result = [];
    if (search) {
      result.push({ field: 'search', operator: 'contains' as const, value: search });
    }
    if (statusFilter !== 'all') {
      result.push({ field: 'status', operator: 'eq' as const, value: statusFilter });
    }
    return result;
  }, [search, statusFilter]);

  // Загрузка приглашений
  const listResult = useList<UserInvitation>({
    resource: 'user-invitations',
    pagination: { currentPage: page, pageSize, mode: 'server' },
    filters: queryFilters,
    sorters: sort ? [{ field: sort.field, order: sort.order }] : [],
  });

  // Список активных Telegram-ботов — для копирования ссылки-приглашения
  // в конкретного бота (`https://t.me/<username>?start=invite_<token>`).
  // `bot-tokens` уже используется в `BotSelect`; кэш staleTime=60s — общий.
  const botsResult = useList<{
    id: number;
    name: string;
    username: string | null;
    isActive: boolean;
  }>({
    resource: 'bot-tokens',
    pagination: { pageSize: 200, mode: 'server' },
    queryOptions: { staleTime: 60_000 },
  });
  const activeBots = useMemo(
    () => (botsResult.query.data?.data ?? []).filter((b) => b.isActive && b.username),
    [botsResult.query.data],
  );

  const data = (listResult.query.data?.data ?? []) as UserInvitation[];
  const total = listResult.query.data?.total ?? 0;
  const isLoading = listResult.query.isLoading;
  const refetchList = listResult.query.refetch;

  // Мутация для массовой отправки
  const bulkSendMutation = useCustomMutation();
  const bulkSend = bulkSendMutation.mutate;
  const isSending = bulkSendMutation.mutation.isPending;

  // Мутация для массового удаления
  const bulkDeleteMutation = useCustomMutation();
  const bulkDelete = bulkDeleteMutation.mutate;
  const isDeleting = bulkDeleteMutation.mutation.isPending;

  const handleBulkSend = (templateId?: number) => {
    // Отправляем PENDING (новые) + SENT EMAIL (повторная отправка письма).
    // Telegram SENT не переотправляем — для них нужен новый инвайт.
    const sendable = selectedInvitations.filter(
      (inv) =>
        inv.status === 'PENDING' ||
        (inv.status === 'SENT' && inv.channel === 'EMAIL'),
    );
    if (sendable.length === 0) {
      notify.error('Нет приглашений для отправки');
      return;
    }

    bulkSend(
      {
        url: '/user-invitations/bulk-send',
        method: 'post',
        values: {
          invitationIds: sendable.map((inv) => inv.id),
          templateId,
        },
      },
      {
        onSuccess: (response) => {
          const result = response.data as { successCount: number; failedCount: number };
          if (result.successCount > 0) {
            notify.success(`Успешно отправлено: ${result.successCount}`);
          }
          if (result.failedCount > 0) {
            notify.error(`Ошибки отправки: ${result.failedCount}`);
          }
          setSendDialogOpen(false);
          setSelectedInvitations([]);
          refetchList();
        },
        onError: () => {
          notify.error('Ошибка при отправке приглашений');
        },
      }
    );
  };

  const handleBulkDelete = () => {
    const deletableInvitations = invitationsToDelete.filter(
      (inv) => inv.status !== 'ACCEPTED'
    );
    if (deletableInvitations.length === 0) {
      notify.error('Нет приглашений для удаления (принятые нельзя удалить)');
      setDeleteDialogOpen(false);
      return;
    }

    bulkDelete(
      {
        url: '/user-invitations/bulk-delete',
        method: 'post',
        values: {
          invitationIds: deletableInvitations.map((inv) => inv.id),
        },
      },
      {
        onSuccess: (response) => {
          const result = response.data as { successCount: number; failedCount: number };
          if (result.successCount > 0) {
            notify.success(`Удалено: ${result.successCount}`);
          }
          if (result.failedCount > 0) {
            notify.error(`Не удалось удалить: ${result.failedCount}`);
          }
          setDeleteDialogOpen(false);
          setInvitationsToDelete([]);
          refetchList();
        },
        onError: () => {
          notify.error('Ошибка при удалении приглашений');
        },
      }
    );
  };

  const handleResetFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setPage(1);
  };

  const copyToClipboard = (url: string, successMessage: string) => {
    void navigator.clipboard.writeText(url).then(
      () => notify.success(successMessage),
      () => notify.error('Не удалось скопировать ссылку'),
    );
  };

  const buildTelegramInviteUrl = (botUsername: string, inviteToken: string): string =>
    `https://t.me/${botUsername}?start=invite_${inviteToken}`;

  const buildTelegramMiniAppInviteUrl = (botUsername: string, inviteToken: string): string =>
    `https://t.me/${botUsername}?startapp=invite_${inviteToken}`;

  const activeFiltersCount = statusFilter !== 'all' ? 1 : 0;

  // Bulk actions.
  // «Отправить приглашения» доступна всегда: для EMAIL-приглашений TG-сессия
  // не нужна, а для TELEGRAM-приглашений сервер сам вернёт ошибку, если
  // у админа нет авторизованной сессии (она появится в результатах bulk-send).
  const bulkActions: BulkAction<UserInvitation>[] = [
    {
      label: 'Отправить приглашения',
      icon: <Send className="h-4 w-4" />,
      onClick: (items: UserInvitation[]) => {
        setSelectedInvitations(items);
        setSendDialogOpen(true);
      },
      showOnlyWhenSelected: true,
    },
    {
      label: 'Удалить',
      icon: <Trash2 className="h-4 w-4" />,
      onClick: (items: UserInvitation[]) => {
        setInvitationsToDelete(items);
        setDeleteDialogOpen(true);
      },
      variant: 'destructive',
      showOnlyWhenSelected: true,
    },
  ];

  // Колонки таблицы
  const columns: Column<UserInvitation>[] = [
    {
      key: 'user',
      header: 'Пользователь',
      width: '250px',
      render: (_, invitation) => {
        const avatarUrl = getAvatarUrl(invitation);
        return (
          <div className="flex items-center gap-3">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt=""
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <User className="h-5 w-5 text-muted-foreground" />
              </div>
            )}
            <div className="flex flex-col min-w-0">
              <span className="font-medium truncate">{getUserName(invitation)}</span>
              {invitation.telegramUsername && (
                <span className="text-sm text-muted-foreground flex items-center gap-1">
                  <AtSign className="h-3 w-3" />
                  {invitation.telegramUsername}
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      key: 'channel',
      header: 'Канал',
      width: '120px',
      render: (_, invitation) => (
        <Badge variant="outline" className="gap-1">
          {invitation.channel === 'EMAIL' ? (
            <AtSign className="h-3 w-3" />
          ) : (
            <Send className="h-3 w-3" />
          )}
          {INVITATION_CHANNEL_LABELS[invitation.channel]}
        </Badge>
      ),
    },
    {
      key: 'telegramPhone',
      header: 'Телефон',
      width: '140px',
      render: (_, invitation) => (
        <div className="flex items-center gap-2 text-sm">
          {invitation.telegramPhone ? (
            <>
              <Phone className="h-3.5 w-3.5 text-muted-foreground" />
              {invitation.telegramPhone}
            </>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Статус',
      width: '160px',
      sortable: true,
      render: (_, invitation) => {
        const isUnresolved =
          invitation.channel === 'TELEGRAM' &&
          invitation.telegramUserId === null &&
          (invitation.status === 'PENDING' || invitation.status === 'SENT');
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            <div className="flex items-center gap-2">
              {getStatusIcon(invitation.status)}
              <Badge variant={INVITATION_STATUS_COLORS[invitation.status]}>
                {INVITATION_STATUS_LABELS[invitation.status]}
              </Badge>
            </div>
            {isUnresolved && (
              <Badge
                variant="outline"
                className="gap-1 text-amber-600 border-amber-300 dark:text-amber-400 dark:border-amber-800"
                title="Резолв через Telegram не выполнялся — доступ выдастся автоматически при регистрации по телефону/username, либо привяжите вручную"
              >
                <AlertCircle className="h-3 w-3" />
                не резолвлено
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      key: 'emailOpen',
      header: 'Открыто',
      width: '140px',
      render: (_, invitation) => {
        // Для TELEGRAM-канала трекинг не применим — показываем прочерк.
        if (invitation.channel !== 'EMAIL') {
          return <span className="text-muted-foreground text-sm">—</span>;
        }
        // Письмо не отправлено или открытий нет.
        if (!invitation.emailFirstOpenedAt) {
          return (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <MailX className="h-3.5 w-3.5" />
              <span>не открыто</span>
            </div>
          );
        }
        const openedAt = formatDate(invitation.emailFirstOpenedAt);
        const count = invitation.emailOpenCount;
        return (
          <div className="flex items-center gap-1.5 text-sm" title={`Первое открытие: ${openedAt}`}>
            <MailOpen className="h-3.5 w-3.5 text-green-500" />
            <span className="font-medium">{openedAt}</span>
            {count > 1 && (
              <Badge variant="outline" className="text-xs px-1.5 py-0">
                ×{count}
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      key: 'items',
      header: 'Доступы',
      width: '240px',
      render: (_, invitation) => {
        const items = invitation.items ?? [];
        if (items.length === 0) {
          // Legacy: до 0093 хранили один абонемент в plan/pricingTier.
          if (invitation.pricingTier) {
            return <PricingTiersList tiers={[invitation.pricingTier]} onlyActive={false} />;
          }
          if (invitation.subscriptionPlan) {
            return <Badge variant="secondary">{invitation.subscriptionPlan.code}</Badge>;
          }
          return <span className="text-muted-foreground">—</span>;
        }

        // Группируем по типу для компактного бейджа: «Абонемент: 1, Курс: 2…»
        const counts = items.reduce<Record<string, number>>((acc, it) => {
          acc[it.targetType] = (acc[it.targetType] ?? 0) + 1;
          return acc;
        }, {});
        const labelByType: Record<string, string> = {
          SUBSCRIPTION_PLAN: 'Абонемент',
          COURSE: 'Курс',
          CHANNEL_PASS: 'Канал',
          BOOK: 'Книга',
        };

        return (
          <HoverCard openDelay={150} closeDelay={80}>
            <HoverCardTrigger asChild>
              <div className="flex flex-wrap gap-1 cursor-default">
                {Object.entries(counts).map(([type, count]) => (
                  <Badge key={type} variant="secondary" className="text-xs">
                    {labelByType[type] ?? type}: {count}
                  </Badge>
                ))}
              </div>
            </HoverCardTrigger>
            <HoverCardContent align="start" className="w-[420px] p-0">
              <div className="px-3 py-2 border-b text-xs font-semibold text-muted-foreground">
                Выдаваемые доступы ({items.length})
              </div>
              {invitation.accessEndsAt && (
                <div className="px-3 py-2 border-b flex items-center gap-1.5 text-xs">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Доступ до:</span>
                  <span className="font-medium">{formatDate(invitation.accessEndsAt)}</span>
                </div>
              )}
              <ul className="max-h-96 overflow-y-auto divide-y">
                {items.map((it, idx) => {
                  const typeLabel = labelByType[it.targetType] ?? it.targetType;
                  const title = it.targetTitle?.trim() || `#${it.targetId}`;
                  const tier = it.pricingTier ?? null;
                  return (
                    <li key={it.id} className="px-3 py-2 text-sm">
                      <div className="flex items-start gap-2">
                        <Badge variant="outline" className="text-[10px] shrink-0 mt-0.5">
                          {typeLabel}
                        </Badge>
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="font-medium truncate" title={title}>{title}</div>
                          {tier ? (
                            <PricingTierBadge tier={tier} index={idx} />
                          ) : it.targetType === 'SUBSCRIPTION_PLAN' ? (
                            <div className="text-xs text-muted-foreground">Тариф не указан</div>
                          ) : null}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </HoverCardContent>
          </HoverCard>
        );
      },
    },
    {
      key: 'accessEndsAt',
      header: 'Доступ до',
      width: '150px',
      render: (_, invitation) => (
        <div className="flex items-center gap-2 text-sm">
          {invitation.accessEndsAt ? (
            <>
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              {formatDate(invitation.accessEndsAt)}
            </>
          ) : (
            <span className="text-muted-foreground">Бессрочно</span>
          )}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Создано',
      width: '150px',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
          {formatDate(String(value))}
        </div>
      ),
    },
    {
      key: 'sentAt',
      header: 'Отправлено',
      width: '150px',
      sortable: true,
      render: (_, invitation) => (
        <span className="text-sm">
          {invitation.sentAt ? formatDate(invitation.sentAt) : '—'}
        </span>
      ),
    },
    {
      key: 'acceptedAt',
      header: 'Принято',
      width: '150px',
      sortable: true,
      render: (_, invitation) => (
        <span className="text-sm">
          {invitation.acceptedAt ? formatDate(invitation.acceptedAt) : '—'}
        </span>
      ),
    },
    {
      key: 'invitedByAdmin',
      header: 'Добавил',
      width: '150px',
      render: (_, invitation) => (
        <span className="text-sm">
          {invitation.invitedByAdmin?.name || invitation.invitedByAdmin?.email || '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '110px',
      render: (_, invitation) => (
        <div className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                title="Скопировать ссылку-приглашение"
                disabled={!invitation.inviteToken}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>Скопировать ссылку</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={!invitation.inviteUrl}
                onSelect={() => {
                  if (!invitation.inviteUrl) {
                    notify.error('Ссылка на платформу недоступна');
                    return;
                  }
                  copyToClipboard(invitation.inviteUrl, 'Ссылка на платформу скопирована');
                }}
              >
                <Globe className="h-4 w-4 mr-2" />
                <div className="flex flex-col">
                  <span>Платформа</span>
                  <span className="text-xs text-muted-foreground">
                    Откроет страницу приглашения
                  </span>
                </div>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                Telegram-бот
              </DropdownMenuLabel>
              {botsResult.query.isLoading ? (
                <DropdownMenuItem disabled>Загрузка ботов…</DropdownMenuItem>
              ) : activeBots.length === 0 ? (
                <DropdownMenuItem disabled>Нет активных ботов</DropdownMenuItem>
              ) : (
                activeBots.map((bot) => (
                  <DropdownMenuItem
                    key={`bot-${bot.id}`}
                    onSelect={() => {
                      if (!invitation.inviteToken || !bot.username) {
                        notify.error('Ссылка для бота недоступна');
                        return;
                      }
                      copyToClipboard(
                        buildTelegramInviteUrl(bot.username, invitation.inviteToken),
                        `Ссылка на @${bot.username} скопирована`,
                      );
                    }}
                  >
                    <Bot className="h-4 w-4 mr-2" />
                    <div className="flex flex-col min-w-0">
                      <span className="truncate">{bot.name}</span>
                      <span className="text-xs text-muted-foreground truncate">
                        @{bot.username}
                      </span>
                    </div>
                  </DropdownMenuItem>
                ))
              )}
              {activeBots.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                    Telegram Mini App
                  </DropdownMenuLabel>
                  {activeBots.map((bot) => (
                    <DropdownMenuItem
                      key={`miniapp-${bot.id}`}
                      onSelect={() => {
                        if (!invitation.inviteToken || !bot.username) {
                          notify.error('Ссылка для Mini App недоступна');
                          return;
                        }
                        copyToClipboard(
                          buildTelegramMiniAppInviteUrl(bot.username, invitation.inviteToken),
                          `Mini App ссылка на @${bot.username} скопирована`,
                        );
                      }}
                    >
                      <AppWindow className="h-4 w-4 mr-2" />
                      <div className="flex flex-col min-w-0">
                        <span className="truncate">{bot.name}</span>
                        <span className="text-xs text-muted-foreground truncate">
                          @{bot.username} · startapp
                        </span>
                      </div>
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="ghost"
            size="sm"
            title="Привязать к пользователю"
            disabled={
              invitation.status === 'ACCEPTED' || invitation.status === 'EXPIRED'
            }
            onClick={() => setAttachDialogInvitation(invitation)}
          >
            <Link2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            title="Открыть приглашение"
            onClick={() => navigate(`/user-invitations/show/${invitation.id}`)}
          >
            <Eye className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <FormHeader
        title="Приглашения пользователей"
        description="Управление приглашениями в систему — через Telegram или email"
        sticky
        icon={<UserPlus className="h-5 w-5" />}
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/user-invitations/import')}
            >
              <Upload className="h-4 w-4 mr-2" />
              Импорт
            </Button>
            <Button variant="outline" onClick={() => setCreateForUserDialogOpen(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Существующему
            </Button>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Добавить
            </Button>
          </div>
        }
      />

      {/*
        Глобальный баннер об отсутствии Telegram-сессии убран: EMAIL-канал
        работает без TG. Если сессии нет — inline-предупреждение появится
        только когда админ выберет канал «Telegram» внутри диалога создания
        приглашения (см. CreateInvitationDialog → блок !hasSession).
       */}

      {/* Поиск и фильтры */}
      <TableToolbar
        searchValue={search}
        onSearchChange={(v) => { setSearch(v); setPage(1); }}
        searchPlaceholder="Поиск по имени, username, email, телефону, telegramId, токену..."
        filtersCount={activeFiltersCount}
        filterTitle="Фильтры приглашений"
        onResetFilters={handleResetFilters}
        filters={
          <div className="space-y-2">
            <Label htmlFor="status-filter">Статус</Label>
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger id="status-filter">
                <SelectValue placeholder="Статус" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Все статусы</SelectItem>
                <SelectItem value="PENDING">Ожидает отправки</SelectItem>
                <SelectItem value="SENT">Отправлено</SelectItem>
                <SelectItem value="ACCEPTED">Принято</SelectItem>
                <SelectItem value="FAILED">Ошибка</SelectItem>
                <SelectItem value="EXPIRED">Истекло</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />

      {/* Таблица */}
      <DataTable
        columns={columns}
        data={data}
        total={total}
        page={page}
        pageSize={pageSize}
        isLoading={isLoading}
        onPageChange={setPage}
        emptyMessage="Нет приглашений"
        selectable
        rowKey="id"
        sort={sort}
        onSortChange={setSort}
        bulkActions={bulkActions}
      />

      {/* Диалог создания приглашения */}
      <CreateInvitationDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={refetchList}
        hasSession={hasSession}
        templates={templates}
        templatesLoading={templatesLoading}
      />

      {/* Диалог создания приглашения для уже зарегистрированного пользователя */}
      <CreateInvitationForUserDialog
        open={createForUserDialogOpen}
        onClose={() => setCreateForUserDialogOpen(false)}
        onCreated={refetchList}
      />

      {/* Диалог привязки приглашения к существующему пользователю */}
      <AttachInvitationToUserDialog
        open={attachDialogInvitation !== null}
        onOpenChange={(next) => {
          if (!next) {
            setAttachDialogInvitation(null);
          }
        }}
        invitation={attachDialogInvitation}
        onAttached={refetchList}
      />

      {/* Диалог отправки */}
      <SendDialog
        open={sendDialogOpen}
        onClose={() => setSendDialogOpen(false)}
        onConfirm={handleBulkSend}
        isLoading={isSending}
        selectedCount={selectedInvitations.filter((inv) => inv.status === 'PENDING').length}
        templates={templates}
        templatesLoading={templatesLoading}
      />

      {/* Диалог подтверждения удаления */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить приглашения?</AlertDialogTitle>
            <AlertDialogDescription>
              {(() => {
                const deletable = invitationsToDelete.filter(
                  (inv) => inv.status !== 'ACCEPTED'
                );
                const skipped = invitationsToDelete.length - deletable.length;
                return (
                  <>
                    Будет удалено {deletable.length} из {invitationsToDelete.length} выбранных приглашений.
                    {skipped > 0 && (
                      <> {skipped} принятых приглашений будут пропущены.</>
                    )}
                    {' '}Это действие нельзя отменить.
                  </>
                );
              })()}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Удаление...' : 'Удалить'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
